import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader, KpiRow, Card, SectionHeading, DataTable, Badge } from "../components/dc";
import { Button } from "../components/ui/button";
import { formatCurrency } from "../lib/format";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { AccessibleModal } from "../components/AccessibleModal";

const GREEN = "#34d399", CYAN = "#67e0f0", RED = "#fb7185", AMBER = "#fbbf24", VIOLET = "#a855f7", MUTE = "#9d8fc0";
const inputStyle = { background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" };
const labelStyle = { fontSize: 12, color: MUTE, marginBottom: 4, display: "block" };
const gbp = (n) => formatCurrency(n || 0);

const STAGE_ORDER = ["data_investigation", "raised_pm", "raised_supplier", "credit_agreed", "mitigated_closed"];
const STAGE_LABEL = { data_investigation: "Data investigation", raised_pm: "Raised with PM", raised_supplier: "Raised with supplier", credit_agreed: "Credit agreed", mitigated_closed: "Mitigated / closed" };
const STAGE_TONE = { data_investigation: "muted", raised_pm: "cyan", raised_supplier: "cyan", credit_agreed: "green", mitigated_closed: "green" };
const REASON_LABEL = { transport: "Transport/carriage in price", data_issue: "Data / UoM issue", written_off: "Written off" };

export default function Credits() {
  usePageTitle("Recovery");
  const [tab, setTab] = useState("overview");
  const [credits, setCredits] = useState([]);
  const [mitigations, setMitigations] = useState([]);
  const [cases, setCases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditForm, setCreditForm] = useState(null);
  const [mitForm, setMitForm] = useState(null);
  const [caseForm, setCaseForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, mi, ca, sup, m] = await Promise.all([
      apiGet("/credits"), apiGet("/mitigations"), apiGet("/recovery/cases"),
      apiGet("/suppliers"), apiGet("/dashboard/supplier-metrics", { timeout: 30000 }),
    ]);
    setCredits(c?.credits || []);
    setMitigations(mi?.mitigations || []);
    setCases(ca?.cases || []);
    setSuppliers(sup?.suppliers || []);
    setMetrics(Array.isArray(m?.metrics) ? m.metrics : []);
  }, []);
  useEffect(() => { (async () => { setLoading(true); try { await load(); } finally { setLoading(false); } })(); }, [load]);

  // Three-way totals from the (overlaid) supplier metrics.
  const totals = useMemo(() => {
    const t = { identified: 0, mitigated: 0, recovered: 0, outstanding: 0 };
    for (const m of metrics) {
      t.identified += m.overcharge_value || 0;
      t.mitigated += m.overcharge_mitigated || 0;
      t.recovered += m.credit_received || 0;
      t.outstanding += m.net_overcharge != null ? m.net_overcharge : (m.overcharge_value || 0);
    }
    return t;
  }, [metrics]);

  const supplierRows = useMemo(() => metrics
    .filter((m) => (m.overcharge_value || 0) > 0 || (m.overcharge_mitigated || 0) > 0 || (m.credit_received || 0) > 0)
    .map((m) => ({
      supplier: m.supplier, identified: m.overcharge_value || 0, mitigated: m.overcharge_mitigated || 0,
      recovered: m.credit_received || 0, outstanding: m.net_overcharge != null ? m.net_overcharge : (m.overcharge_value || 0),
      over: m.over_recovered,
    }))
    .sort((a, b) => b.identified - a.identified), [metrics]);

  const scopeLabel = (sc) => {
    if (!sc || sc.type === "supplier") return "Supplier-wide";
    if (sc.type === "month") return `Month ${sc.value}`;
    if (sc.type === "sku") return `SKU ${sc.value}`;
    if (sc.type === "invoice") { const n = (sc.value || "").split(",").filter((x) => x.trim()).length; return `${n} invoice${n === 1 ? "" : "s"}`; }
    return sc.value || "—";
  };

  // Default-to-scope: fetch the identified overcharge for a form's scope and set amount.
  const prefill = async (f, setF) => {
    if (!f.supplier) return;
    try {
      const d = await apiGet("/recovery/scope-overcharge", { params: { supplier: f.supplier, type: f.scopeType, value: f.scopeValue || "" } });
      if (d && d.identified != null) setF((x) => ({ ...x, amount: String(d.identified) }));
    } catch { /* leave as-is */ }
  };

  const remove = async (path, label, reload) => { if (!window.confirm(`Delete this ${label}?`)) return; try { await apiDelete(path); toast.success(`${label} deleted.`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); } };

  if (loading) return (<div><PageHeader title="Recovery" subtitle="Overcharge recovery, mitigation & supplier cases" /><Card><div style={{ color: MUTE, padding: 24 }}>Loading…</div></Card></div>);

  const addBtn = { overview: null,
    credits: <Button className="btn btn-primary" onClick={() => setCreditForm({ supplier: "", amount: "", reference: "", credit_date: "", status: "received", scopeType: "supplier", scopeValue: "", note: "" })} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={15} /> Log credit</Button>,
    mitigations: <Button className="btn btn-primary" onClick={() => setMitForm({ supplier: "", amount: "", reason: "transport", scopeType: "supplier", scopeValue: "", mitigated_date: "", note: "" })} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={15} /> Log mitigation</Button>,
    cases: <Button className="btn btn-primary" onClick={() => setCaseForm({ title: "", supplier: "", scopeType: "supplier", scopeValue: "", owner: "", stage: "data_investigation", amount: "", reason: "transport", note: "" })} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={15} /> New case</Button>,
  }[tab];

  return (
    <div data-testid="recovery-page">
      <PageHeader title="Recovery" subtitle="What's been recovered, mitigated, and still to chase — plus the supplier approach tracker" right={addBtn} />

      <KpiRow items={[
        { label: "Overcharge identified", value: gbp(totals.identified), valueColor: RED },
        { label: "Mitigated (explained)", value: gbp(totals.mitigated), valueColor: AMBER },
        { label: "Recovered (credited)", value: gbp(totals.recovered), valueColor: GREEN },
        { label: "Still outstanding", value: gbp(totals.outstanding), valueColor: totals.outstanding > 0 ? RED : GREEN },
      ]} />

      <div style={{ display: "inline-flex", gap: 2, background: "#0f0a1e", border: "1px solid var(--hairline)", borderRadius: 999, padding: 3, margin: "6px 0 16px" }}>
        {[["overview", "Overview"], ["credits", `Credits (${credits.length})`], ["mitigations", `Mitigations (${mitigations.length})`], ["cases", `Cases (${cases.length})`]].map(([k, lbl]) => (
          <button data-testid={`recovery-tab-${k}`} aria-pressed={tab === k} key={k} onClick={() => setTab(k)} style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 600, background: tab === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent", color: tab === k ? "#fff" : MUTE }}>{lbl}</button>
        ))}
      </div>

      {tab === "overview" && (
        <Card>
          <SectionHeading title="By supplier" subtitle="Identified → Mitigated + Recovered → Still outstanding" />
          <DataTable perPage={12} searchKeys={["supplier"]} searchPlaceholder="Search supplier…"
            columns={[
              { key: "supplier", label: "Supplier", width: "1.6fr" },
              { key: "identified", label: "Identified", align: "right", width: "1fr", render: (r) => <span style={{ color: r.identified > 0 ? RED : MUTE }}>{gbp(r.identified)}</span> },
              { key: "mitigated", label: "Mitigated", align: "right", width: "1fr", render: (r) => <span style={{ color: r.mitigated > 0 ? AMBER : MUTE }}>{r.mitigated > 0 ? gbp(r.mitigated) : "—"}</span> },
              { key: "recovered", label: "Recovered", align: "right", width: "1fr", render: (r) => <span style={{ color: r.recovered > 0 ? GREEN : MUTE, fontWeight: 600 }}>{r.recovered > 0 ? gbp(r.recovered) : "—"}</span> },
              { key: "outstanding", label: "Outstanding", align: "right", width: "1.1fr", render: (r) => <span style={{ fontWeight: 700, color: r.over ? AMBER : r.outstanding > 0 ? RED : GREEN }}>{r.over ? "Over-applied" : gbp(r.outstanding)}</span> },
            ]}
            rows={supplierRows} />
        </Card>
      )}

      {tab === "credits" && (
        <Card>
          <SectionHeading title="Logged credits" subtitle={`${credits.length} credit${credits.length === 1 ? "" : "s"} — money clawed back`} />
          <DataTable perPage={15} searchKeys={["supplier", "reference"]} searchPlaceholder="Search supplier or reference…"
            columns={[
              { key: "supplier", label: "Supplier", width: "1.4fr" },
              { key: "amount", label: "Amount", align: "right", width: "0.9fr", render: (r) => <span style={{ fontWeight: 700 }}>{gbp(r.amount)}</span> },
              { key: "status", label: "Status", width: "0.9fr", render: (r) => <Badge tone={r.status === "received" ? "green" : "cyan"}>{r.status === "received" ? "Received" : "Expected"}</Badge> },
              { key: "scope", label: "Scope", width: "1.1fr", render: (r) => <span style={{ fontSize: 12.5, color: MUTE }}>{scopeLabel(r.scope)}</span> },
              { key: "credit_date", label: "Date", width: "0.9fr", render: (r) => r.credit_date || "—" },
              { key: "created_by", label: "By", width: "1fr", render: (r) => <span style={{ fontSize: 12.5, color: MUTE }}>{r.created_by || "—"}</span> },
              { key: "actions", label: "", width: "0.7fr", align: "right", render: (r) => (
                <span style={{ display: "inline-flex", gap: 8 }}>
                  <button title="Edit" onClick={() => setCreditForm({ id: r.id, supplier: r.supplier, amount: String(r.amount), reference: r.reference || "", credit_date: r.credit_date || "", status: r.status || "received", scopeType: r.scope?.type || "supplier", scopeValue: r.scope?.value || "", note: r.note || "" })} style={{ background: "none", border: "none", cursor: "pointer", color: MUTE }}><Pencil size={15} /></button>
                  <button title="Delete" onClick={() => remove(`/credits/${r.id}`, "credit")} style={{ background: "none", border: "none", cursor: "pointer", color: RED }}><Trash2 size={15} /></button>
                </span>) },
            ]}
            rows={credits} />
          {credits.length === 0 && <div style={{ color: MUTE, fontSize: 14, padding: 8 }}>No credits logged yet.</div>}
        </Card>
      )}

      {tab === "mitigations" && (
        <Card>
          <SectionHeading title="Mitigations" subtitle="Overcharge investigation showed isn't recoverable — nets off, no money moves" />
          <DataTable perPage={15} searchKeys={["supplier"]} searchPlaceholder="Search supplier…"
            columns={[
              { key: "supplier", label: "Supplier", width: "1.4fr" },
              { key: "amount", label: "Amount", align: "right", width: "0.9fr", render: (r) => <span style={{ fontWeight: 700, color: AMBER }}>{gbp(r.amount)}</span> },
              { key: "reason", label: "Reason", width: "1.3fr", render: (r) => REASON_LABEL[r.reason] || r.reason },
              { key: "scope", label: "Scope", width: "1.1fr", render: (r) => <span style={{ fontSize: 12.5, color: MUTE }}>{scopeLabel(r.scope)}</span> },
              { key: "mitigated_date", label: "Date", width: "0.9fr", render: (r) => r.mitigated_date || "—" },
              { key: "created_by", label: "By", width: "1fr", render: (r) => <span style={{ fontSize: 12.5, color: MUTE }}>{r.created_by || "—"}</span> },
              { key: "actions", label: "", width: "0.7fr", align: "right", render: (r) => (
                <span style={{ display: "inline-flex", gap: 8 }}>
                  <button title="Edit" onClick={() => setMitForm({ id: r.id, supplier: r.supplier, amount: String(r.amount), reason: r.reason || "transport", scopeType: r.scope?.type || "supplier", scopeValue: r.scope?.value || "", mitigated_date: r.mitigated_date || "", note: r.note || "" })} style={{ background: "none", border: "none", cursor: "pointer", color: MUTE }}><Pencil size={15} /></button>
                  <button title="Delete" onClick={() => remove(`/mitigations/${r.id}`, "mitigation")} style={{ background: "none", border: "none", cursor: "pointer", color: RED }}><Trash2 size={15} /></button>
                </span>) },
            ]}
            rows={mitigations} />
          {mitigations.length === 0 && <div style={{ color: MUTE, fontSize: 14, padding: 8 }}>No mitigations logged yet.</div>}
        </Card>
      )}

      {tab === "cases" && (
        <Card>
          <SectionHeading title="Supplier approach cases" subtitle="Data investigation → Raised with PM → Raised with supplier → Credit agreed / Mitigated" />
          <DataTable perPage={12} searchKeys={["supplier", "title", "owner"]} searchPlaceholder="Search supplier / title / owner…"
            columns={[
              { key: "title", label: "Case", width: "1.6fr", render: (r) => <span><strong style={{ color: "var(--color-text-1)" }}>{r.title || r.supplier}</strong>{r.title ? <span style={{ color: MUTE, fontSize: 12 }}> · {r.supplier}</span> : null}</span> },
              { key: "scope", label: "Scope", width: "1fr", render: (r) => <span style={{ fontSize: 12.5, color: MUTE }}>{scopeLabel(r.scope)}</span> },
              { key: "owner", label: "Owner", width: "1fr", render: (r) => r.owner || "—" },
              { key: "stage", label: "Stage", width: "1.2fr", render: (r) => <Badge tone={STAGE_TONE[r.stage] || "muted"}>{STAGE_LABEL[r.stage] || r.stage}</Badge> },
              { key: "amount", label: "Amount", align: "right", width: "0.9fr", render: (r) => r.amount > 0 ? gbp(r.amount) : "—" },
              { key: "actions", label: "", width: "0.7fr", align: "right", render: (r) => (
                <span style={{ display: "inline-flex", gap: 8 }}>
                  <button title="Edit" onClick={() => setCaseForm({ id: r.id, title: r.title || "", supplier: r.supplier, scopeType: r.scope?.type || "supplier", scopeValue: r.scope?.value || "", owner: r.owner || "", stage: r.stage || "data_investigation", amount: r.amount ? String(r.amount) : "", reason: r.reason || "transport", note: r.note || "" })} style={{ background: "none", border: "none", cursor: "pointer", color: MUTE }}><Pencil size={15} /></button>
                  <button title="Delete" onClick={() => remove(`/recovery/cases/${r.id}`, "case")} style={{ background: "none", border: "none", cursor: "pointer", color: RED }}><Trash2 size={15} /></button>
                </span>) },
            ]}
            rows={cases} />
          {cases.length === 0 && <div style={{ color: MUTE, fontSize: 14, padding: 8 }}>No cases yet. Click <strong style={{ color: "var(--color-text-1)" }}>New case</strong> to start one.</div>}
        </Card>
      )}

      {/* ── Credit form ── */}
      {creditForm && (
        <Modal title={creditForm.id ? "Edit credit" : "Log a credit"} onClose={() => !saving && setCreditForm(null)}
          onSave={async () => {
            if (!creditForm.supplier) return toast.error("Pick a supplier.");
            if (!(Number(creditForm.amount) > 0)) return toast.error("Enter an amount > 0.");
            setSaving(true);
            try {
              const body = { supplier: creditForm.supplier, amount: Number(creditForm.amount), reference: creditForm.reference, credit_date: creditForm.credit_date, status: creditForm.status, scope: { type: creditForm.scopeType, value: creditForm.scopeValue.trim() }, note: creditForm.note };
              if (creditForm.id) await apiPut(`/credits/${creditForm.id}`, body); else await apiPost("/credits", body);
              toast.success("Saved."); setCreditForm(null); await load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setSaving(false); }
          }} saving={saving} saveLabel={creditForm.id ? "Save changes" : "Log credit"}>
          <SupplierField f={creditForm} setF={setCreditForm} suppliers={suppliers} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <AmountField f={creditForm} setF={setCreditForm} onPrefill={prefill} />
            <div><label style={labelStyle}>Status</label>
              <select value={creditForm.status} onChange={(e) => setCreditForm({ ...creditForm, status: e.target.value })} style={inputStyle}>
                <option value="received">Received (nets)</option><option value="expected">Expected (display only)</option>
              </select></div>
          </div>
          <ScopeField f={creditForm} setF={setCreditForm} />
          <NoteField f={creditForm} setF={setCreditForm} />
        </Modal>
      )}

      {/* ── Mitigation form ── */}
      {mitForm && (
        <Modal title={mitForm.id ? "Edit mitigation" : "Log a mitigation"} onClose={() => !saving && setMitForm(null)}
          onSave={async () => {
            if (!mitForm.supplier) return toast.error("Pick a supplier.");
            if (!(Number(mitForm.amount) > 0)) return toast.error("Enter an amount > 0.");
            setSaving(true);
            try {
              const body = { supplier: mitForm.supplier, amount: Number(mitForm.amount), reason: mitForm.reason, scope: { type: mitForm.scopeType, value: mitForm.scopeValue.trim() }, mitigated_date: mitForm.mitigated_date, note: mitForm.note };
              if (mitForm.id) await apiPut(`/mitigations/${mitForm.id}`, body); else await apiPost("/mitigations", body);
              toast.success("Saved."); setMitForm(null); await load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setSaving(false); }
          }} saving={saving} saveLabel={mitForm.id ? "Save changes" : "Log mitigation"}>
          <SupplierField f={mitForm} setF={setMitForm} suppliers={suppliers} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <AmountField f={mitForm} setF={setMitForm} onPrefill={prefill} />
            <div><label style={labelStyle}>Reason</label>
              <select value={mitForm.reason} onChange={(e) => setMitForm({ ...mitForm, reason: e.target.value })} style={inputStyle}>
                {Object.entries(REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
          </div>
          <ScopeField f={mitForm} setF={setMitForm} />
          <NoteField f={mitForm} setF={setMitForm} />
        </Modal>
      )}

      {/* ── Case form ── */}
      {caseForm && (
        <Modal title={caseForm.id ? "Edit case" : "New recovery case"} onClose={() => !saving && setCaseForm(null)}
          onSave={async () => {
            if (!caseForm.supplier) return toast.error("Pick a supplier.");
            setSaving(true);
            try {
              const body = { title: caseForm.title, supplier: caseForm.supplier, scope: { type: caseForm.scopeType, value: caseForm.scopeValue.trim() }, owner: caseForm.owner, stage: caseForm.stage, amount: Number(caseForm.amount) || 0, reason: caseForm.reason, note: caseForm.note };
              if (caseForm.id) await apiPut(`/recovery/cases/${caseForm.id}`, body); else await apiPost("/recovery/cases", body);
              toast.success("Saved."); setCaseForm(null); await load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setSaving(false); }
          }} saving={saving} saveLabel={caseForm.id ? "Save changes" : "Create case"}>
          <div><label style={labelStyle}>Title (optional)</label>
            <input value={caseForm.title} onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })} style={inputStyle} placeholder="e.g. CEF Q1 overcharge" /></div>
          <SupplierField f={caseForm} setF={setCaseForm} suppliers={suppliers} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={labelStyle}>Owner</label>
              <input value={caseForm.owner} onChange={(e) => setCaseForm({ ...caseForm, owner: e.target.value })} style={inputStyle} placeholder="who's driving it" /></div>
            <div><label style={labelStyle}>Stage</label>
              <select value={caseForm.stage} onChange={(e) => setCaseForm({ ...caseForm, stage: e.target.value })} style={inputStyle}>
                {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
              </select></div>
          </div>
          <ScopeField f={caseForm} setF={setCaseForm} />
          <div style={{ display: "grid", gridTemplateColumns: caseForm.stage === "mitigated_closed" ? "1fr 1fr" : "1fr", gap: 12 }}>
            <AmountField f={caseForm} setF={setCaseForm} onPrefill={prefill} labelText="Amount (credited / mitigated)" />
            {caseForm.stage === "mitigated_closed" && (
              <div><label style={labelStyle}>Mitigation reason</label>
                <select value={caseForm.reason} onChange={(e) => setCaseForm({ ...caseForm, reason: e.target.value })} style={inputStyle}>
                  {Object.entries(REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
            )}
          </div>
          {(caseForm.stage === "credit_agreed" || caseForm.stage === "mitigated_closed") && (
            <div style={{ fontSize: 12, color: AMBER }}>Saving at this stage will {caseForm.stage === "credit_agreed" ? "log a credit" : "log a mitigation"} of {gbp(Number(caseForm.amount) || 0)} against {scopeLabel({ type: caseForm.scopeType, value: caseForm.scopeValue })}.</div>
          )}
          <NoteField f={caseForm} setF={setCaseForm} />
        </Modal>
      )}
    </div>
  );
}

// ── shared form bits ──
function Modal({ title, onClose, onSave, saving, saveLabel, children }) {
  return (
    <AccessibleModal title={title} onClose={onClose} testId="recovery-modal" width={540}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700 }}>{title}</div>
          <button data-testid="recovery-modal-close" aria-label="Close" onClick={onClose} style={{ background: "none", border: "none", color: MUTE, cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gap: 14 }}>{children}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
          <Button data-testid="recovery-modal-cancel" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="recovery-modal-save" className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? "Saving…" : saveLabel}</Button>
        </div>
    </AccessibleModal>
  );
}
function SupplierField({ f, setF, suppliers }) {
  return (<div><label style={labelStyle}>Supplier</label>
    <select data-testid="recovery-supplier" aria-label="Supplier" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} style={inputStyle}>
      <option value="">Select supplier…</option>{suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
    </select></div>);
}
function AmountField({ f, setF, onPrefill, labelText = "Amount (£)" }) {
  return (<div><label style={labelStyle}>{labelText}</label>
    <div style={{ display: "flex", gap: 6 }}>
      <input data-testid="recovery-amount" aria-label="Amount in GBP" type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} style={inputStyle} placeholder="0.00" />
      <button data-testid="recovery-prefill-amount" type="button" title="Use identified overcharge for this scope" onClick={() => onPrefill(f, setF)} style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: VIOLET, cursor: "pointer", fontSize: 12, padding: "0 10px", whiteSpace: "nowrap" }}>= scope</button>
    </div></div>);
}
function ScopeField({ f, setF }) {
  const [invoices, setInvoices] = useState([]);
  const [loadingInv, setLoadingInv] = useState(false);
  useEffect(() => {
    if (f.scopeType !== "invoice" || !f.supplier) { setInvoices([]); return; }
    setLoadingInv(true);
    apiGet("/recovery/supplier-invoices", { params: { supplier: f.supplier } })
      .then((d) => setInvoices(d?.invoices || [])).catch(() => setInvoices([])).finally(() => setLoadingInv(false));
  }, [f.scopeType, f.supplier]);
  const selected = new Set((f.scopeValue || "").split(",").map((s) => s.trim()).filter(Boolean));
  const toggle = (ref) => { const n = new Set(selected); n.has(ref) ? n.delete(ref) : n.add(ref); setF({ ...f, scopeValue: Array.from(n).join(", ") }); };
  const twoCol = f.scopeType === "month" || f.scopeType === "sku";
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: twoCol ? "1fr 1fr" : "1fr", gap: 12 }}>
        <div><label style={labelStyle}>Applies to</label>
          <select data-testid="recovery-scope" aria-label="Recovery scope" value={f.scopeType} onChange={(e) => setF({ ...f, scopeType: e.target.value, scopeValue: "" })} style={inputStyle}>
            <option value="supplier">Whole supplier</option>
            <option value="month">Specific month</option>
            <option value="sku">Specific SKU</option>
            <option value="invoice">Specific invoice(s)</option>
          </select></div>
        {twoCol && (
          <div><label style={labelStyle}>{f.scopeType === "month" ? "Month (YYYY-MM)" : "SKU"}</label>
            <input value={f.scopeValue} onChange={(e) => setF({ ...f, scopeValue: e.target.value })} style={inputStyle} placeholder={f.scopeType === "month" ? "2026-06" : "ABC123"} /></div>
        )}
      </div>
      {f.scopeType === "invoice" && (
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>Invoices{selected.size ? ` · ${selected.size} selected` : ""}</label>
          {!f.supplier ? <div style={{ fontSize: 12, color: MUTE }}>Pick a supplier first.</div>
            : loadingInv ? <div style={{ fontSize: 12, color: MUTE }}>Loading invoices…</div>
            : invoices.length === 0 ? <div style={{ fontSize: 12, color: MUTE }}>No overcharged invoices for this supplier.</div>
            : (<div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #2a1c47", borderRadius: 8 }}>
                {invoices.map((iv, i) => (
                  <label key={iv.invoice_reference} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", padding: "6px 10px", cursor: "pointer", borderTop: i ? "1px solid rgba(42,28,71,0.5)" : "none", fontSize: 12.5 }}>
                    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                      <input type="checkbox" checked={selected.has(iv.invoice_reference)} onChange={() => toggle(iv.invoice_reference)} />
                      <span style={{ fontFamily: "monospace" }}>{iv.invoice_reference}</span>
                      <span style={{ color: MUTE }}>{iv.date ? String(iv.date).slice(0, 10) : ""}</span>
                    </span>
                    <span style={{ color: RED }}>{gbp(iv.overcharge)}</span>
                  </label>
                ))}
              </div>)}
        </div>
      )}
    </div>
  );
}
function NoteField({ f, setF }) {
  return (<div><label style={labelStyle}>Note (optional)</label>
    <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} style={{ ...inputStyle, minHeight: 56, resize: "vertical" }} placeholder="Context, dispute ref, what was found…" /></div>);
}
